import axios from 'axios'
import client from '@js/client'
import resources from '@js/resources'
import { CACHE_SIZES, INPUT_MIN_LENGTHS, SEARCH_EXPERT_PARAMS } from '@js/constants'
import { camelKeyToSnakeKey, snakeKeyToCamelKeyRecursive, } from '@js/str-utils'

const getDefaultState = () => {
  return {
    cancellers: {},
    skillCache: [],
    skillSuggestionCache: [],
  };
};

const skills = {
  namespaced: true,
  state: getDefaultState(),
  getters: {
    cacheExists: state => tenantUserId => {
      return state.skillCache.length > 0 ? state.skillCache.filter(elem => elem.tenantUserId === tenantUserId).length > 0 : false;
    },
    skillSuggestionCacheExists: state => input => {
      if (state.skillSuggestionCache.length === 0) {
        return false;
      }
      return state.skillSuggestionCache.filter(elem => elem.input.toLowerCase() === input.toLowerCase()).length > 0;
    },
    skills: state => tenantUserId => {
      return state.skillCache.length > 0 ? state.skillCache.filter(elem => elem.tenantUserId === tenantUserId)[0].data : [];
    },
    skillSuggestions: state => input => {
      return state.skillSuggestionCache.filter(elem => elem.input.toLowerCase() === input.toLowerCase())[0].suggestions;
    },
    edges: state => tenantUserId => {
      if (state.skillCache.length === 0) {
        return []
      }

      const skills = state.skillCache.filter(elem => elem.tenantUserId === tenantUserId)[0].data;
      return [...new Set(
        skills.reduce((parent, elem) => {
          const edges = elem.targets.reduce((child, skillId) => {
            const pair1 = { source: elem.id, target: skillId };
            const pair2 = { source: skillId, target: elem.id };
            child.push(JSON.stringify(pair1, Object.keys(pair1).sort()))
            child.push(JSON.stringify(pair2, Object.keys(pair2).sort()))
            return child;
          }, []);
          parent.push(...edges);
          return parent;
        }, []))].map(elem => JSON.parse(elem));
    },
  },
  mutations: {
    initialize (state) {
      Object.assign(state, getDefaultState());
    },
    setSkills (state, { tenantUserId, skills }) {
      state.skillCache = state.skillCache.filter(elem => elem.tenantUserId !== tenantUserId);
      if (state.skillCache.length >= CACHE_SIZES.SKILL) {
        state.skillCache.shift();
      }
      state.skillCache.push({ tenantUserId: tenantUserId, data: skills });
    },
    setSkillSuggestions (state, { input, suggestions }) {
      state.skillSuggestionCache = state.skillSuggestionCache.filter(elem => elem.input.toLowerCase() !== input.toLowerCase());
      if (state.skillSuggestionCache.length >= CACHE_SIZES.SKILL_SUGGESTION) {
        state.skillSuggestionCache.shift();
      }
      state.skillSuggestionCache.push({ input: input.toLowerCase(), suggestions: suggestions });
    },
  },
  actions: {
    async createSkill ({ rootGetters }, { name }) {
      const tenantDomain = await rootGetters['tenant/getTenantDomain'];
      const payload = await { name: name };
      const res = await client.post(resources['skills'](tenantDomain), payload);
      return res;
    },
    async fetchSkillSuggestions({ state, commit, rootGetters }, input) {
      if (input.length < INPUT_MIN_LENGTHS.STRICT_SKILL_SUGGESTION) {
        return [];
      }

      const tenantDomain = await rootGetters['tenant/getTenantDomain'];
      const payload = await {
        inputData: input,
        length: SEARCH_EXPERT_PARAMS.SUGGESTION_LENGTH,
        strict: input.length < INPUT_MIN_LENGTHS.SKILL_SUGGESTION,
      };

      if (state.cancellers.skillSuggestions) {
        await state.cancellers.skillSuggestions();
        await delete state.cancellers.skillSuggestions;
      }
      const res = await client.post(
        resources['searchExpertsInputSuggestions'](tenantDomain),
        camelKeyToSnakeKey(payload),
        {
          cancelToken: new axios.CancelToken(function executor(c) {
            state.cancellers.skillSuggestions = c;
          }),
        },
      );
      const suggestions = await res.data.reduce((result, elem) => {
        delete elem.tenant;
        elem.display = elem.name;
        result.push(snakeKeyToCamelKeyRecursive(elem));
        return result;
      }, []);

      await commit('setSkillSuggestions', { input, suggestions });
      return suggestions;
    },
  },
}

export default skills;
